/**
 * The GitHub post-install redirect, parsed and redeemed. Pure (no React, no
 * network client), so it is tested without a DOM.
 *
 * GitHub returns three query params the backend needs to bind the
 * installation: the OAuth `code`, the `installation_id`, and the `state` the
 * install link carried (issued by POST /github/auth/state to this user of this
 * workspace; single-use). A redirect without a state was not started from
 * SystemDox (a forged or replayed link), so it is never sent at all.
 */

export interface GitHubCallback {
  code: string;
  installationId: number;
  state: string;
}

export const INCOMPLETE_CALLBACK_MESSAGE =
  'This GitHub connection was not started from SystemDox, or GitHub did not return '
  + 'everything needed. Start again from Settings > GitHub.';

/**
 * Returns null when a param is missing or the id is not a positive integer
 * (GitHub also appends setup_action etc. — ignored here).
 */
export function parseGitHubCallback(params: URLSearchParams): GitHubCallback | null {
  const code = params.get('code');
  const state = params.get('state');
  const installationId = Number(params.get('installation_id'));
  if (!code || !state || !Number.isInteger(installationId) || installationId <= 0) return null;
  return { code, installationId, state };
}

/**
 * The redemption flow: parse → redeem exactly once → report. `flight`
 * persists across effect re-runs (React StrictMode double-invokes effects in
 * dev), so the single-use ?code and state are never POSTed twice — a second
 * redemption fails on both GitHub's side and ours and would flash an error
 * over a successful install. A failed redemption keeps `flight` consumed: the
 * code and state are spent either way, so retrying can only add noise.
 */
export function redeemGitHubCallback(
  params: URLSearchParams,
  flight: { redeemed: boolean },
  io: {
    redeem: (code: string, installationId: number, state: string) => Promise<unknown>;
    onSuccess: () => void;
    onError: (message: string) => void;
  },
): void {
  const callback = parseGitHubCallback(params);
  if (!callback) {
    io.onError(INCOMPLETE_CALLBACK_MESSAGE);
    return;
  }
  if (flight.redeemed) return;
  flight.redeemed = true;

  io.redeem(callback.code, callback.installationId, callback.state)
    .then(() => io.onSuccess())
    .catch((err) => {
      io.onError(err?.response?.data?.message || err?.message || 'OAuth callback failed');
    });
}
